/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {Component} from 'react';
import {error} from '@ion/core/src/logger/logger';
import {ErrorHandlerProps} from '../components/errorhandler/ErrorHandler';
import {RequiredPropsWithChildren} from '../types/components';
import ErrorState from './ErrorState';

/* eslint-disable react/jsx-handler-names */

type State = {
  error: Error | null;
};

interface Props extends RequiredPropsWithChildren, ErrorHandlerProps {
};

/**
 * Provides the standard DLS ErrorBoundary component which is to be displayed when
 * a client-side runtime error occurs.
 *
 * @see https://www.figma.com/file/CjFWcqy1RlydaKnQhNlgVR/CalAmp-Design-Explorations?node-id=88%3A2757
 *
 * ```typescript
 *   import {ErrorBoundary} from '@ion/dls/src/notifications/ErrorBoundary';
 *
 *   const App = () => {
 *     return (
 *       <I18nextProvider i18n={i18n}>
 *         <ThemeProvider theme={theme}>
 *           <ErrorBoundary>
 *             {initialized && <Component {...pageProps} />}
 *           </ErrorBoundary>
 *         </ThemeProvider>
 *       </I18nextProvider>
 *     );
 *   }
 *
 * ```
 *
 */
export class ErrorBoundary extends Component<Props, State> {
  /**
   * @private
   */
  state = {error: null}

  /**
   * @private
   */
  onRefresh = (e) => {
    if (this.props.onRefresh) {
      this.props.onRefresh(e);
      this.setState({error: null});
    } else {
      window.location.reload();
    }
  }

  /**
   * @private
   */
  componentDidCatch(e, info) {
    error(`Unhandled Runtime Error: ${e.message}`, info);
    this.setState({error: e});
    this.props.onError?.(e);
  }

  /**
   * @private
   */
  render() {
    if (this.state.error) {
      return (
        <div style={{marginTop: '15%'}}>
          <ErrorState onRefresh={this.onRefresh}
            errorText={this.props.errorText}
            isRTE
            show />
        </div>
      );
    }
    return this.props.children;
  }
}
